import React, { useEffect } from 'react';
import { Form, Input, Select, Button, Card, Typography, Space, Spin, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser, useCreateUser, useUpdateUser } from '@/hooks/useUsers';
import { useCompanies } from '@/hooks/useCompanies';
import { extractErrorMessage } from '@/utils/helpers';

const { Title } = Typography;

const UserForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const { data: user, isLoading: isLoadingUser } = useUser(id || '');
  const { data: companiesData, isLoading: isLoadingCompanies } = useCompanies();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  useEffect(() => {
    if (user) {
      form.setFieldsValue(user);
    }
  }, [user, form]);

  useEffect(() => {
    console.log('Companies data:', companiesData);
  }, [companiesData]);

  const onFinish = async (values: any) => {
    try {
      if (id) {
        await updateUser.mutateAsync({ id, data: values });
        message.success('Usuário atualizado com sucesso!');
      } else {
        await createUser.mutateAsync(values);
        message.success('Usuário criado com sucesso!');
      }
      navigate('/users');
    } catch (error: any) {
      message.error(extractErrorMessage(error, 'Erro ao salvar usuário'));
    }
  };

  const isLoading = createUser.isPending || updateUser.isPending;

  return (
    <div>
      <Title level={4}>{id ? 'Editar Usuário' : 'Novo Usuário'}</Title>
      
      <Card>
        <Spin spinning={isLoadingUser || isLoadingCompanies}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              profile: 'VIEWER',
            }}
          >
            <Form.Item
              name="first_name"
              label="Nome"
              rules={[{ required: true, message: 'Por favor, insira o nome' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="last_name"
              label="Sobrenome"
              rules={[{ required: true, message: 'Por favor, insira o sobrenome' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="E-mail"
              rules={[
                { required: true, message: 'Por favor, insira o e-mail' },
                { type: 'email', message: 'E-mail inválido' },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="username"
              label="Nome de Usuário"
              rules={[{ required: true, message: 'Por favor, insira o nome de usuário' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Telefone"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="company"
              label="Empresa"
            >
              <Select 
                allowClear 
                placeholder="Selecione a empresa"
                loading={isLoadingCompanies}
                showSearch
                optionFilterProp="children"
              >
                {companiesData?.results?.map((company) => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.trade_name || company.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="profile"
              label="Perfil"
              rules={[{ required: true, message: 'Por favor, selecione o perfil' }]}
            >
              <Select>
                <Select.Option value="SUPER_ADMIN">Super Admin</Select.Option>
                <Select.Option value="ADMIN">Admin</Select.Option>
                <Select.Option value="OPERATOR">Operador</Select.Option>
                <Select.Option value="VIEWER">Visualizador</Select.Option>
              </Select>
            </Form.Item>

            {!id && (
              <>
                <Form.Item
                  name="password"
                  label="Senha"
                  rules={[
                    { required: true, message: 'Por favor, insira a senha' },
                    { min: 8, message: 'A senha deve ter pelo menos 8 caracteres' },
                  ]}
                >
                  <Input.Password />
                </Form.Item>

                <Form.Item
                  name="password_confirm"
                  label="Confirmar Senha"
                  rules={[
                    { required: true, message: 'Por favor, confirme a senha' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('As senhas não conferem'));
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </>
            )}

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Salvar
                </Button>
                <Button onClick={() => navigate('/users')}>
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default UserForm;
